/**
 * Setup Organization Wizard
 *
 * One-time page shown immediately after an OLTRIAL user signs up.
 * Collects the org name, founding year, sports, and a suggested 3-char
 * join code. On submit → POST /api/organizations/setup which stamps the
 * user as AD of the new org and clears `pending_org_setup`.
 *
 * Route guard: if the current user does NOT have `pending_org_setup`,
 * bounce them to `/dashboard`.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, ArrowRight, RefreshCcw, CheckCircle2, XCircle, Trophy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SPORT_OPTIONS = ['Basketball', 'Baseball', 'Softball', 'Volleyball', 'Soccer', 'Football', 'Lacrosse', 'Wrestling', 'Track & Field', 'Swimming'];
const JOIN_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function randomCode(len = 3) {
  let s = '';
  for (let i = 0; i < len; i++) s += JOIN_CODE_ALPHABET[Math.floor(Math.random() * JOIN_CODE_ALPHABET.length)];
  return s;
}

export default function SetupOrgPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState(null); // null=checking, true=needs setup, false=bounce
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    founding_year: new Date().getFullYear(),
    sports: [],
    join_code: randomCode(),
  });

  // Route guard — only OLTRIAL-newly-registered users with the pending
  // flag can land here. Anyone else gets redirected home.
  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/auth/me`);
        if (data?.pending_org_setup) setPending(true);
        else { setPending(false); navigate('/dashboard', { replace: true }); }
      } catch {
        setPending(false); navigate('/login', { replace: true });
      }
    })();
  }, [user, loading, navigate]);

  const toggleSport = (s) => {
    setForm(p => ({
      ...p,
      sports: p.sports.includes(s) ? p.sports.filter(x => x !== s) : [...p.sports, s],
    }));
  };

  const codeValid = useMemo(() => /^[A-Z0-9]{3}$/.test(form.join_code), [form.join_code]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) return setError('Organization name is required');
    if (form.sports.length === 0) return setError('Pick at least one sport');
    if (!codeValid) return setError('Join code must be exactly 3 letters or numbers');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/organizations/setup`, {
        name: form.name.trim(),
        founding_year: Number(form.founding_year),
        sports: form.sports,
        join_code: form.join_code.toUpperCase(),
      });
      // Full reload so AuthContext re-reads the user with the new role + org
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not create organization');
      setSubmitting(false);
    }
  };

  if (pending !== true) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-slate-100 flex items-center justify-center px-6 py-10" data-testid="setup-org-page">
      <div className="w-full max-w-xl">
        <div className="flex items-center gap-2 mb-6">
          <Trophy className="h-6 w-6 text-amber-500" />
          <div>
            <h1 className="text-2xl font-semibold">Create your organization</h1>
            <p className="text-sm text-slate-400">Welcome to OmniLeagues. Let's set up your org — this only takes a minute.</p>
          </div>
        </div>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6">
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Organization name</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="South Wake Sabres"
                  className="bg-slate-950 border-slate-800 text-slate-100 h-11 mt-1"
                  data-testid="setup-name" />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Founding year</Label>
                <Input type="number" min={1900} max={new Date().getFullYear() + 1}
                  value={form.founding_year}
                  onChange={e => setForm(p => ({ ...p, founding_year: e.target.value }))}
                  className="bg-slate-950 border-slate-800 text-slate-100 h-11 mt-1 w-40"
                  data-testid="setup-year" />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Sports</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SPORT_OPTIONS.map(s => {
                    const on = form.sports.includes(s);
                    return (
                      <button key={s} type="button" onClick={() => toggleSport(s)}
                        className={`px-3 py-1.5 rounded-full text-xs border transition ${
                          on ? 'bg-amber-500 border-amber-500 text-slate-900 font-semibold'
                             : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600'
                        }`}
                        data-testid={`sport-${s.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-2">
                  Family join code
                  <span className="text-[10px] normal-case tracking-normal text-slate-500">Parents enter this to sign up under your org</span>
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input value={form.join_code}
                    onChange={e => setForm(p => ({ ...p, join_code: e.target.value.toUpperCase().slice(0, 3) }))}
                    maxLength={3}
                    className={`bg-slate-950 border-slate-800 text-slate-100 h-11 w-24 font-mono text-lg tracking-[0.3em] text-center ${codeValid ? '' : 'border-red-500/50'}`}
                    data-testid="setup-join-code" />
                  <Button type="button" variant="ghost" size="sm" onClick={() => setForm(p => ({ ...p, join_code: randomCode() }))}
                    className="text-slate-400 hover:text-slate-200" data-testid="setup-regenerate">
                    <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Re-roll
                  </Button>
                  {codeValid ? (
                    <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> valid
                    </Badge>
                  ) : (
                    <Badge className="bg-red-500/15 text-red-400 border border-red-500/30">
                      <XCircle className="h-3 w-3 mr-1" /> invalid
                    </Badge>
                  )}
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="setup-error">
                  {error}
                </div>
              )}

              <Button type="submit" disabled={submitting}
                className="w-full h-12 bg-[#1e3a8a] hover:bg-[#1e40af] text-white tracking-[0.15em] text-sm"
                data-testid="setup-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>CREATE ORGANIZATION <ArrowRight className="h-4 w-4 ml-2" /></>}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
